import { APP_NAME, SUPPORT_EMAIL } from "../app.constants";

export interface SeoPage {
  title: string;
  eyebrow: string;
  description: string;
  sections: { title: string; text: string }[];
}

export const SEO_SITE_PAGES: Record<string, SeoPage> = {
  features: {
    eyebrow: `THE ${APP_NAME.toUpperCase()} TOOLKIT`,
    title: `Features made for real conversations`,
    description:
      "Interest matching, guest access, privacy controls, and simple chat tools help you focus on the person you are meeting.",
    sections: [
      {
        title: `Interest-based matching`,
        text: `Add topics you enjoy. ${APP_NAME} prioritises matching you with people who share them, so every conversation starts in familiar territory.`,
      },
      {
        title: `Guest access`,
        text: "No account required. Pick a nickname and start chatting in seconds.",
      },
      {
        title: "Skip and block",
        text: "Move on from any conversation instantly. Blocked users are excluded from future matches in the same session.",
      },
    ],
  },
  support: {
    eyebrow: `HELP CENTER`,
    title: `${APP_NAME} support`,
    description: `Find answers about guest sessions, matching, safety tools, and using ${APP_NAME} responsibly.`,
    sections: [
      {
        title: `Getting started`,
        text: "Pick a nickname, choose your interests, and tap 'Find a match'. No account or app download needed.",
      },
      {
        title: "Safety and moderation",
        text: `Use the skip button to move on, the block button to exclude someone permanently, or contact ${SUPPORT_EMAIL} for serious concerns.`,
      },
      {
        title: "Chat persistence",
        text: "Your chat history is saved in your browser only. Clearing your browser data will remove it. It is never stored on our servers.",
      },
    ],
  },
  contact: {
    eyebrow: `GET IN TOUCH`,
    title: `Contact ${APP_NAME}`,
    description: `Have a question, feedback, or safety concern? Reach out to the ${APP_NAME} team.`,
    sections: [
      {
        title: `General enquiries`,
        text: `Email us at ${SUPPORT_EMAIL} for questions about the platform, features, or your experience.`,
      },
      {
        title: "Safety and abuse reports",
        text: `For urgent safety concerns or to report abusive behaviour, email ${SUPPORT_EMAIL} with as much detail as possible.`,
      },
      {
        title: "Business and press",
        text: `For partnership or media enquiries, reach out via ${SUPPORT_EMAIL} with a brief description of your request.`,
      },
    ],
  },
  privacy: {
    eyebrow: `YOUR PRIVACY`,
    title: `Privacy at ${APP_NAME}`,
    description: `Learn what information ${APP_NAME} uses to provide chat, matching, safety, and reliable service.`,
    sections: [
      {
        title: `No personal data collected`,
        text: `${APP_NAME} does not collect your name, email, or phone number. A session nickname is all that is required.`,
      },
      {
        title: `Chat is not stored`,
        text: `Messages are relayed in real time and not stored on ${APP_NAME} servers after the session ends.`,
      },
      {
        title: `Your browser, your data`,
        text: "Chat history is saved only in your browser's local storage. Clearing your browser data removes it completely.",
      },
    ],
  },
  terms: {
    eyebrow: `THE BASICS`,
    title: `${APP_NAME} terms of service`,
    description: `The rules and responsibilities that keep ${APP_NAME} useful and respectful for everyone.`,
    sections: [
      {
        title: `Use ${APP_NAME} responsibly`,
        text: `${APP_NAME} is for adults aged 18 and over. You agree to treat other users with respect and to follow the community guidelines.`,
      },
      {
        title: `Prohibited content`,
        text: "Sexual content, harassment, threats, and illegal activity are prohibited. Violations may result in a session ban.",
      },
      {
        title: `Service availability`,
        text: `${APP_NAME} is provided as-is. We do our best to keep it running but cannot guarantee uninterrupted availability.`,
      },
    ],
  },
  "community-guidelines": {
    eyebrow: "OUR COMMUNITY",
    title: `Community guidelines`,
    description: `Help make ${APP_NAME} welcoming: be respectful, protect privacy, and never use the service to harm or exploit others.`,
    sections: [
      {
        title: `Be respectful`,
        text: "Treat every person you meet as you would want to be treated. Harassment, threats, and hate speech are never acceptable.",
      },
      {
        title: "Protect privacy",
        text: "Do not share personal details about yourself or ask for them from others. Respect that people choose to remain anonymous.",
      },
      {
        title: "Report violations",
        text: `If you encounter content that breaks these guidelines, use the in-chat report button or contact ${SUPPORT_EMAIL}.`,
      },
    ],
  },
};
